/* v8 ignore next */
import type {
    GetFilterTypesCommandInput,
    GetThemesRequest,
    DiscoveryGetContentCategoriesRequest,
    DiscoveryGetContentDetailsRequest,
    DiscoveryGetFileDownloadUrlRequest,
    DiscoverySearchRequest,
    RequestBody,
    GetFilterTypesResponse,
    DiscoveryGetFileDownloadUrlResponse,
    DiscoverySearchResponse,
    DiscoveryGetContentDetailsResponse,
    DiscoveryGetContentCategoriesResponse,
    DiscoveryGetPitchedContentResponse,
    DiscoveryGetPitchedContentRequest,
    DiscoveryGetFileUploadUrlRequest,
    DiscoveryGetFileUploadUrlResponse,
    DiscoveryPutContentDetailsRequest,
    DiscoveryPutContentDetailsResponse,
    GetExternalShareUrlRequest,
    GetExternalShareUrlResponse,
    ContentDetailsData,
    ExternalShareType,
    DiscoveryGetPermissionForUserRequest,
    DiscoveryGetPermissionForUserResponse,
} from "@amzn/ads-discovery-theme-service-typescript-client";
import {
    GetFilterTypesCommand,
    GetThemesCommand,
    GetContentCategoriesCommand,
    AdsDiscoveryThemeClient,
    GetContentDetailsCommand,
    GetFileDownloadUrlCommand,
    SearchContentsCommand,
    GetPitchedContentCommand,
    GetFileUploadUrlCommand,
    PutContentDetailsCommand,
    GetExternalShareUrlCommand,
    GetPermissionForUserCommand,
} from "@amzn/ads-discovery-theme-service-typescript-client";
import { getCredentialsFromCacheOrCognito } from "@amzn/midway-cognito-type-script-provider";

import { authStore } from "../../store/auth";
import type { ContentPreviewType } from "../../types/content";
import { APPENV } from "../../utils/app.environment";
import { getClientId, getCustomerId } from "../adsApi/adsApi.config";

export class DiscoveryApiClient {
    public client: AdsDiscoveryThemeClient;

    constructor(client: AdsDiscoveryThemeClient) {
        this.client = client;
    }

    getFilterTypes: () => Promise<GetFilterTypesResponse> = async () => {
        const request: GetFilterTypesCommandInput = {};
        return this.client.send(new GetFilterTypesCommand(request));
    };

    getPitchedContent: () => Promise<DiscoveryGetPitchedContentResponse> =
        async () => {
            const request: DiscoveryGetPitchedContentRequest = {
                AmazonAdvertisingAPIClientId: getClientId(),
                customerId: getCustomerId(),
                userAlias: authStore.getState().username,
                isInternal: true,
            };
            return this.client.send(new GetPitchedContentCommand(request));
        };

    getThemes: (request: GetThemesRequest) => Promise<any> = async (
        request,
    ) => {
        return this.client.send(new GetThemesCommand(request));
    };

    getContentCategories: (
        request: string[],
    ) => Promise<DiscoveryGetContentCategoriesResponse> = async (
        contentCategoryIds,
    ) => {
        const request: DiscoveryGetContentCategoriesRequest = {
            AmazonAdvertisingAPIClientId: getClientId(),
            customerId: getCustomerId(),
            userAlias: authStore.getState().username,
            request: {
                contentCategoryIds,
            },
            isInternal: true,
        };
        return this.client.send(new GetContentCategoriesCommand(request));
    };

    getContentDetails: (
        contentId: string,
        previewType: ContentPreviewType | string,
        isUserPreview?: boolean,
    ) => Promise<DiscoveryGetContentDetailsResponse> = async (
        contentId,
        previewType,
        isUserPreview = false,
    ) => {
        const request: DiscoveryGetContentDetailsRequest = {
            AmazonAdvertisingAPIClientId: getClientId(),
            customerId: getCustomerId(),
            userAlias: authStore.getState().username,
            isInternal: true,
            contentId,
            isUserPreview,
            previewType,
        };
        return this.client.send(new GetContentDetailsCommand(request));
    };

    putContentDetails: (
        contentId: string,
        contentDetails: ContentDetailsData,
        accountOpportunities: string[],
    ) => Promise<DiscoveryPutContentDetailsResponse> = async (
        contentId,
        contentDetails,
        accountOpportunities,
    ) => {
        const request: DiscoveryPutContentDetailsRequest = {
            AmazonAdvertisingAPIClientId: getClientId(),
            customerId: getCustomerId(),
            userAlias: authStore.getState().username,
            isInternal: true,
            contentId,
            contentDetails,
            accountOpportunities,
        };
        return this.client.send(new PutContentDetailsCommand(request));
    };

    getFileUploadUrl: (
        fileName: string,
        contentId?: string,
    ) => Promise<DiscoveryGetFileUploadUrlResponse> = async (
        fileName,
        contentId,
    ) => {
        const fileUploadRequest = {
            AmazonAdvertisingAPIClientId: getClientId(),
            customerId: getCustomerId(),
            userAlias: authStore.getState().username,
            isInternal: true,
            fileName,
        };

        // Provide content id in request if declared
        const request: DiscoveryGetFileUploadUrlRequest = contentId
            ? { ...fileUploadRequest, contentId }
            : fileUploadRequest;

        return this.client.send(new GetFileUploadUrlCommand(request));
    };

    getExternalShareUrl: (
        contentId: string,
        version: number,
        type: ExternalShareType,
    ) => Promise<GetExternalShareUrlResponse> = async (
        contentId,
        version,
        type,
    ) => {
        const request: GetExternalShareUrlRequest = {
            AmazonAdvertisingAPIClientId: getClientId(),
            customerId: getCustomerId(),
            userAlias: authStore.getState().username,
            isInternal: true,
            contentId,
            version,
            type,
        };
        return this.client.send(new GetExternalShareUrlCommand(request));
    };

    getContentDownloadUrl: (
        contentId: string,
        version: string,
    ) => Promise<DiscoveryGetFileDownloadUrlResponse> = async (
        contentId,
        version,
    ) => {
        const request: DiscoveryGetFileDownloadUrlRequest = {
            AmazonAdvertisingAPIClientId: getClientId(),
            customerId: getCustomerId(),
            userAlias: authStore.getState().username,
            isInternal: true,
            contentId,
            // For some reason the type is being casted as number by default
            version: version.toString(),
        };
        return this.client.send(new GetFileDownloadUrlCommand(request));
    };

    searchContents: (
        requestBody: RequestBody,
    ) => Promise<DiscoverySearchResponse> = async (requestBody) => {
        const request: DiscoverySearchRequest = {
            AmazonAdvertisingAPIClientId: getClientId(),
            customerId: getCustomerId(),
            userAlias: authStore.getState().username,
            isInternal: true,
            request: requestBody,
        };
        return this.client.send(new SearchContentsCommand(request));
    };

    getPermissionForUser: () => Promise<DiscoveryGetPermissionForUserResponse> =
        async () => {
            const request: DiscoveryGetPermissionForUserRequest = {
                customerId: getCustomerId(),
                userAlias: authStore.getState().username,
            };
            return this.client.send(new GetPermissionForUserCommand(request));
        };
}

export async function getDiscoveryApiClient(): Promise<DiscoveryApiClient> {
    try {
        const {
            settings: { cognitoPoolId, region, discoveryApiEndpoint },
        } = authStore.getState();

        if (!discoveryApiEndpoint) {
            throw new Error("Discovery endpoint not yet defined");
        }

        const { accessKeyId, secretAccessKey, sessionToken } =
            await getCredentialsFromCacheOrCognito({
                region,
                identityPoolId: cognitoPoolId,
                clientId: window.location.host,
                redirectUri: window.location.href,
            });

        const client = new AdsDiscoveryThemeClient({
            endpoint: `${
                APPENV.mock_server ? "http" : "https"
            }://${discoveryApiEndpoint}`,
            region,
            credentials: { accessKeyId, secretAccessKey, sessionToken },
        });

        return new DiscoveryApiClient(client);
    } catch (e: any) {
        throw new Error(e.message);
    }
}
